/**
 * SQL Column Constants for V2 Hooks
 *
 * Centralized column selections for Supabase queries.
 * Using explicit columns instead of SELECT * optimizes bandwidth.
 *
 * IMPORTANT: These must match the actual database schema.
 * Run `npm test -- schemaValidation` to verify columns match the schema.
 *
 * When updating these constants:
 * 1. Check the actual table schema in Supabase
 * 2. Regenerate types with `npx supabase gen types typescript`
 * 3. Update the constant and corresponding test
 */

// committees_v2 table columns
export const COMMITTEES_V2_COLUMNS =
  'id, state_id, session_id, name, chamber, committee_type, chair_term_id, vice_chair_term_id, ranking_minority_term_id, meeting_day, meeting_time, meeting_location, url, assistant_name, assistant_email, assistant_phone, created_at, updated_at';

// committee_members_v2 table columns
export const COMMITTEE_MEMBERS_V2_COLUMNS =
  'id, committee_id, term_id, role, start_date, end_date, reason_ended, created_at';

// legislator_terms_v2 table columns (subset used in queries)
export const LEGISLATOR_TERMS_V2_COLUMNS =
  'id, legislator_id, session_id, chamber, district, county, party_id, start_date, end_date, created_at, updated_at';

// legislators_v2 table columns (subset used in queries)
export const LEGISLATORS_V2_COLUMNS =
  'id, state_id, name, first_name, last_name, picture_url, url, ballotpedia_url, wikipedia_url, capitol_office_email, capitol_office_phone, occupation, created_at, updated_at';

// bill_summaries_v2 table columns
export const BILL_SUMMARIES_V2_COLUMNS =
  'id, bill_version_id, summary_type_id, summary_text, summary_json, model_used, tokens_used, created_at';

// bill_sponsors_v2 table columns
export const BILL_SPONSORS_V2_COLUMNS =
  'id, bill_id, term_id, sponsor_type, sponsor_order, created_at';

// bill_status_history_v2 table columns
export const BILL_STATUS_HISTORY_V2_COLUMNS =
  'id, bill_id, status_id, chamber, history_date, raw_status, journal_page, created_at';

// documents_v2 table columns
export const DOCUMENTS_V2_COLUMNS =
  'id, session_id, document_type, title, url, r2_key, published_date, created_at, updated_at';

// bills_v2 table columns
export const BILLS_V2_COLUMNS =
  'id, state_id, session_id, number, chamber, title, url, introduced, status_id, created_at, updated_at';

// bill_versions_v2 table columns (excludes large text columns for bandwidth)
export const BILL_VERSIONS_V2_COLUMNS =
  'id, bill_id, version_date, version_url, chamber, status, r2_key, effective_title_id, pdf_extracted, analysis_complete, standard_summaries_complete, full_summary_complete, created_at, updated_at';

// bill_analysis_v2 table columns
export const BILL_ANALYSIS_V2_COLUMNS =
  'id, bill_version_id, analysis_type_id, user_id, score, rating, rating_json, categories, tags, summary_text, detailed_analysis, confidence_score, model_used, tokens_used, processing_time_ms, analyzed_at, created_at, updated_at';

// bill_title_history_v2 table columns
export const BILL_TITLE_HISTORY_V2_COLUMNS =
  'id, bill_id, title, short_title, effective_date, reason, substitutes_bill_id, sequence_number, notes, bill_version_id, created_at';

// document_summaries_v2 table columns
export const DOCUMENT_SUMMARIES_V2_COLUMNS =
  'id, document_id, document_type, narrative_summary, structured_data, model_used, created_at';

// votes_v2 table columns
export const VOTES_V2_COLUMNS =
  'id, bill_id, version_id, session_id, vote_url, vote_type, vote_date, chamber, yeas, nays, present, absent, passed, created_at';

// legislator_votes_v2 table columns
export const LEGISLATOR_VOTES_V2_COLUMNS = 'id, vote_id, term_id, vote_cast, created_at';

// alert_preferences table columns
export const ALERT_PREFERENCES_COLUMNS =
  'id, user_id, email_enabled, digest_frequency, digest_slot, alert_on_new_bill, alert_on_new_version, alert_on_status_change, alert_on_title_change, alert_on_similar_bill, created_at, updated_at';

// user_focus_profiles table columns
export const USER_FOCUS_PROFILES_COLUMNS = 'id, focus_areas, created_at, updated_at';

// saved_searches table columns
export const SAVED_SEARCHES_COLUMNS =
  'id, user_id, name, search_type, query, filters, session_id, alerts_enabled, alert_frequency, last_viewed_at, last_alert_at, created_at, updated_at';

// user_summary_unlocks table columns
export const USER_SUMMARY_UNLOCKS_COLUMNS = 'id, user_id, bill_id, unlocked_at, expires_at';

// sessions_v2 table columns
export const SESSIONS_V2_COLUMNS =
  'id, state_id, session_code, session_type, display_name, biennium_start_year, biennium_end_year, start_date, end_date, is_active, is_current, parent_session_id, special_session_number, created_at, updated_at';

// federal_legislators table columns
export const FEDERAL_LEGISLATORS_COLUMNS =
  'id, bioguide_id, full_name, first_name, last_name, party, state, chamber, district, state_rank, start_date, end_date, picture_url, office_address, office_phone, website_url, contact_form_url, ballotpedia_url, wikipedia_url, fec_id, govtrack_id, created_at, updated_at';

// profiles table columns
export const PROFILES_COLUMNS =
  'id, subscription_status, beehiiv_user_id, mailerlite_subscriber_id, stripe_customer_id, stripe_subscription_id, stripe_subscription_status, created_at, updated_at';

// committee_meeting_items_v2 table columns
export const COMMITTEE_MEETING_ITEMS_V2_COLUMNS =
  'id, meeting_id, item_order, item_type, raw_text, bill_id, bill_number, bill_title, description, presenter_name, presenter_title, presenter_organization, analyst_name, agency_name, created_at';

// floor_calendar_items_v2 table columns
export const FLOOR_CALENDAR_ITEMS_V2_COLUMNS =
  'id, floor_calendar_id, item_order, section, raw_text, bill_id, bill_number, bill_title, description, action_type, committee_name, status, created_at';

// legislator_terms_v2 filter subset (for filter option queries)
export const LEGISLATOR_TERMS_V2_FILTER_COLUMNS = 'district, county, chamber, party_id';

// user_address table columns
export const USER_ADDRESS_COLUMNS =
  'id, city, state, zip_code, state_house_district, state_senate_district, congressional_district, created_at, updated_at';

// user_legislators table columns
export const USER_LEGISLATORS_COLUMNS =
  'id, legislator_type, state_legislator_id, federal_legislator_id, chamber, created_at';

// legislators_v2 brief subset (for vote stats display)
export const LEGISLATORS_V2_BRIEF_COLUMNS = 'id, name, first_name, last_name, picture_url';

// legislator_terms_v2 brief subset (for vote stats display)
export const LEGISLATOR_TERMS_V2_BRIEF_COLUMNS = 'id, chamber, district, party_id';

// committee_meeting_items_v2 bill reference subset (for calendar bill tracking)
export const COMMITTEE_MEETING_ITEMS_V2_BILL_COLUMNS =
  'meeting_id, bill_id, bill_number, bill_title';

// floor_calendar_items_v2 bill reference subset (for calendar bill tracking)
export const FLOOR_CALENDAR_ITEMS_V2_BILL_COLUMNS =
  'floor_calendar_id, bill_id, bill_number, bill_title';

// states table columns
export const STATES_COLUMNS = 'id, code, name';

// bill_topics table columns (with policy_topics join)
export const BILL_TOPICS_COLUMNS = 'relevance, is_primary, policy_topics(name)';

// pipeline_runs table columns
export const PIPELINE_RUNS_COLUMNS =
  'id, queue_message_id, job_type, status, params, result, error, attempt, started_at, completed_at, continuation_token';
